import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';

import LoginModel from '../../../../../models/user.model';
import { AppCommon } from '../../../../../providers/app-common';
import { SourcingNeedV2ViewModel } from '../../../../../models-views/sourcing-needs-v2.view.model';
import { StandardResponseModel } from '../../../../../models/project.model';

@Component({
  selector: 'app-sn-form-av-v2',
  templateUrl: './sn-form-av-v2.component.html',
  styleUrls: ['./sn-form-av-v2.component.scss'],
  standalone: false
})
export class SnFormAvV2Component implements OnInit, OnChanges {
  @Input() eventId: number | null = null;
  @Input() data: any;
  @Output() closeModal = new EventEmitter<'success' | 'cancel'>();
  
  form!: FormGroup;
  isEdit: boolean = true; // Always edit in modal
  isView: boolean = false; // Never view in modal
  existingData: any = null;

  microphonesResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());
  audioEquipmentAudioResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());
  supportInfraResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());
  avStaffingResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());
  displayUnitsResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());
  camerasAndRecordingResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());
  stageLightingResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());
  videoEquipmentsResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());
  specialLightingResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());
  lightingControlResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());
  riggingAndSupportResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());
  speakerAndAudioResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());

  private readonly arrayFields: string[] = [
    'microphone',
    'speakers_audio_output',
    'audio_equipment',
    'support_infra',
    'av_staff',
    'general_stage_lighting',
    'special_lighting',
    'lighting_control',
    'rigging_support',
    'display_units',
    'cameras_recording',
    'video_equipment'
  ];

  constructor(
    private fb: FormBuilder, 
    private vm: SourcingNeedV2ViewModel,
    private cdr: ChangeDetectorRef
  ) {}


  ngOnInit(): void {
    this.vm.parentSnFormAvV2Component = this;
    this.initForm();
    
    // Subscribe to all option models
    this.vm.subscribeToAvRequirements(
      this.microphonesResponseModel,
      this.audioEquipmentAudioResponseModel,
      this.supportInfraResponseModel,
      this.avStaffingResponseModel,
      this.displayUnitsResponseModel,
      this.camerasAndRecordingResponseModel,
      this.stageLightingResponseModel,
      this.videoEquipmentsResponseModel,
      this.specialLightingResponseModel,
      this.lightingControlResponseModel,
      this.riggingAndSupportResponseModel,
      this.speakerAndAudioResponseModel
    );
    
    // Load options first
    this.vm.initGeneralCallsForAvRequirements();
    this.eventId = this.eventId;
    console.log("Event Id in AV modal::",this.eventId);
    // Wait for options to load before handling input data
    setTimeout(() => {
      this.handleInputData();
    }, 300);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.handleInputData();
    }
  }

  handleInputData(): void {
    if (this.data) {
      this.existingData = this.data;
      this.isView = false;
      this.isEdit = true;
      
      const mappedData = this.mapApiToForm(this.data);
     
      this.form.patchValue(mappedData);
    } else {
      this.existingData = null;
      this.isView = false;
      this.isEdit = true;
      this.form.reset();
      this.form.enable();
      this.cdr.detectChanges();
    }
  }

  // loadExistingData removed; handled by handleInputData

  mapApiToForm(data: any): any {
    // Map API response to form fields if needed (identity mapping for now)
    return {
      microphone: data.microphone || [],
      speakers_audio_output: data.speakers_audio_output || [],
      audio_equipment: data.audio_equipment || [],
      support_infra: data.support_infra || [],
      av_staff: data.av_staff || [],
      general_stage_lighting: data.general_stage_lighting || [],
      special_lighting: data.special_lighting || [],
      lighting_control: this.mapArrayControl(data.lighting_control, data.lighting_control_details),
      rigging_support: data.rigging_support || [],
      display_units: data.display_units || [],
      cameras_recording: data.cameras_recording || [],
      video_equipment: data.video_equipment || [],
      audio_requirements_note: data.audio_requirements_note || '',
      video_requirements_note: data.video_requirements_note || '',
      lighting_requirements_note: data.lighting_requirements_note || ''
    };
  }

  initForm(): void {
    this.form = this.fb.group({
      microphone: [[]],
      speakers_audio_output: [[]],
      audio_equipment: [[]],
      support_infra: [[]],
      av_staff: [[]],
      general_stage_lighting: [[]],
      special_lighting: [[]],
      lighting_control: [[]],
      rigging_support: [[]],
      display_units: [[]],
      cameras_recording: [[]],
      video_equipment: [[]],
      audio_requirements_note: [''],
      video_requirements_note: [''],
      lighting_requirements_note: ['']
    });
  }

  onSave(): void {
    console.log("event id",this.eventId)
    if (this.form.valid) {
      const payload = this.normalizePayload(this.form.value);
      payload.sourcing_need = this.eventId;
      payload.is_av_required = true;
      const loggedInUser = AppCommon.getloggedInObject();
      const userId = loggedInUser?.user?.id;
      
      if (this.isEdit && this.existingData) {
        payload.id = this.existingData.id;
        payload.modified_by = userId;
        this.vm.addUpdateAvRequirements(payload, true);
      } else {
        payload.created_by = userId;
        this.vm.addUpdateAvRequirements(payload, false);
      }
    }
  }

  AvRequirementsAdded() {
    // Success callback - refresh parent data
    this.closeModal.emit();
    
    // Refresh parent component data
    if (this.eventId) {
      this.vm.getSouringNeedbyId(this.eventId, 'cet-sourcing-needs-v2');
    }
  }

  onEdit(): void {
    this.isEdit = true;
    this.isView = false;
    this.form.enable();
  }

  onCancel(): void {
    this.closeModal.emit();
  }

  private normalizePayload(payload: any): any {
    const normalizedPayload = { ...payload };

    this.arrayFields.forEach((field) => {
      normalizedPayload[field] = Array.isArray(normalizedPayload[field]) ? normalizedPayload[field] : [];
    });

    return normalizedPayload;
  }

  private mapArrayControl(rawValue: any, detailsValue: any): any[] {
    if (Array.isArray(rawValue)) {
      return rawValue;
    }

    if (Array.isArray(detailsValue)) {
      return detailsValue.map((item: any) => item.id);
    }

    return [];
  }
}